/**
 * O que a pessoa lê quando o acesso recusa alguma coisa: entrar, definir a
 * senha do convite, trocar a senha ou trocar o e-mail.
 *
 * Por que existe: as telas de acesso mostravam o texto cru do serviço de login,
 * em inglês ("Invalid login credentials", "Password is known to be weak and
 * easy to guess..."). Isso não diz à pessoa o que fazer e expõe o interior do
 * sistema (`.claude/rules/user-facing-communication.md`). Com a proteção contra
 * senha vazada ligada, quem escolhesse uma senha conhecida leria justamente uma
 * dessas frases.
 *
 * Três regras moram aqui, e nenhuma tela deve reimplementá-las:
 *
 * 1. **O código estruturado decide.** `code`, `status`, `name` e os motivos da
 *    senha fraca (`reasons`) vêm primeiro; o texto da mensagem só é lido como
 *    reserva, para erro sem código (servidor antigo, falha de rede).
 * 2. **Toda frase diz o que fazer**, em português, sem nome técnico nem de
 *    fornecedor. Erro desconhecido ganha frase útil, nunca o próprio texto.
 * 3. **Esta função não registra nada.** Quem chama manda o erro original para
 *    o console antes de mostrar a frase.
 */

/** O que deu errado, do ponto de vista de quem está tentando entrar. */
export type AuthErrorKind =
  | "invalid_credentials"
  | "leaked_password"
  | "password_too_short"
  | "password_needs_variety"
  | "weak_password"
  | "same_password"
  | "rate_limited"
  | "email_not_confirmed"
  | "email_in_use"
  | "email_invalid"
  | "session_expired"
  | "access_blocked"
  | "network"
  | "unknown"

/** Em que tela a recusa aconteceu. Muda o que a pessoa pode fazer. */
export type AuthErrorContext = "login" | "set_password" | "change_password" | "change_email"

/** Forma mínima de um erro do serviço de login (AuthError do supabase-js). */
interface AuthErrorLike {
  code?: unknown
  status?: unknown
  name?: unknown
  message?: unknown
  reasons?: unknown
}

export const SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre de novo no CreditOS e tente outra vez."

const INVITE_LINK_EXPIRED_MESSAGE =
  "O link de acesso expirou ou já foi usado. Peça um novo em “Esqueci minha senha”, na tela de entrada, ou peça ao administrador da sua unidade um novo convite."

const SUPPORT_HINT = "Tente de novo em instantes; se continuar, avise o suporte."

const GENERIC_BY_CONTEXT: Record<AuthErrorContext, string> = {
  login: `Não consegui entrar agora. ${SUPPORT_HINT}`,
  set_password: `Não consegui definir a senha agora. ${SUPPORT_HINT}`,
  change_password: `Não consegui trocar a senha agora. ${SUPPORT_HINT}`,
  change_email: `Não consegui trocar o e-mail agora. ${SUPPORT_HINT}`,
}

const SESSION_CODES = new Set([
  "session_expired",
  "session_not_found",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "bad_jwt",
  "invalid_jwt",
  "no_authorization",
  "otp_expired",
  "flow_state_expired",
  "flow_state_not_found",
  "reauthentication_needed",
])

const RATE_LIMIT_CODES = new Set([
  "over_request_rate_limit",
  "over_email_send_rate_limit",
  "over_sms_send_rate_limit",
])

const EMAIL_IN_USE_CODES = new Set(["email_exists", "user_already_exists", "identity_already_exists"])

const EMAIL_INVALID_CODES = new Set(["email_address_invalid", "email_address_not_authorized"])

function asObject(error: unknown): AuthErrorLike {
  if (error && typeof error === "object") return error as AuthErrorLike
  if (typeof error === "string") return { message: error }
  return {}
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

/** Qual regra de senha a recusa apontou, pelos motivos ou, na falta deles, pelo texto. */
function classifyWeakPassword(reasons: unknown, message: string): AuthErrorKind {
  const list = Array.isArray(reasons) ? reasons.filter((r): r is string => typeof r === "string") : []
  // A senha vazada vem primeiro: trocar o tamanho não resolve uma senha conhecida.
  if (list.includes("pwned")) return "leaked_password"
  if (list.includes("length")) return "password_too_short"
  if (list.includes("characters")) return "password_needs_variety"
  return classifyByText(message) ?? "weak_password"
}

/** Reserva para erro sem código: lê o texto que o serviço costuma mandar. */
function classifyByText(rawMessage: string): AuthErrorKind | null {
  const msg = rawMessage.toLowerCase()
  if (!msg) return null
  if (msg.includes("invalid login credentials") || msg.includes("invalid credentials"))
    return "invalid_credentials"
  if (msg.includes("pwned") || msg.includes("known to be weak") || msg.includes("leaked"))
    return "leaked_password"
  if (/at least \d+ characters?/.test(msg) || msg.includes("password should be at least"))
    return "password_too_short"
  if (msg.includes("should contain at least one character")) return "password_needs_variety"
  if (msg.includes("should be different from the old password") || msg.includes("same password"))
    return "same_password"
  if (msg.includes("rate limit") || msg.includes("too many requests") || msg.includes("too many"))
    return "rate_limited"
  if (msg.includes("email not confirmed")) return "email_not_confirmed"
  if (msg.includes("already been registered") || msg.includes("already registered") || msg.includes("already exists"))
    return "email_in_use"
  if (
    msg.includes("session missing") ||
    msg.includes("session expired") ||
    msg.includes("sessão expirada") ||
    msg.includes("jwt expired") ||
    msg.includes("invalid jwt") ||
    msg.includes("expired or is invalid")
  )
    return "session_expired"
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network request failed") || msg.includes("load failed"))
    return "network"
  return null
}

/** Classifica a recusa do serviço de login. Nunca lança. */
export function classifyAuthError(error: unknown): AuthErrorKind {
  const e = asObject(error)
  const code = asString(e.code)
  const name = asString(e.name)
  const message = asString(e.message)
  const status = typeof e.status === "number" ? e.status : undefined

  if (code === "weak_password" || name === "AuthWeakPasswordError")
    return classifyWeakPassword(e.reasons, message)

  if (code === "invalid_credentials") return "invalid_credentials"
  if (code === "same_password") return "same_password"
  if (code === "email_not_confirmed") return "email_not_confirmed"
  if (code === "user_banned") return "access_blocked"
  if (code === "request_timeout") return "network"
  if (RATE_LIMIT_CODES.has(code)) return "rate_limited"
  if (EMAIL_IN_USE_CODES.has(code)) return "email_in_use"
  if (EMAIL_INVALID_CODES.has(code)) return "email_invalid"
  if (SESSION_CODES.has(code)) return "session_expired"

  if (name === "AuthSessionMissingError" || name === "AuthInvalidJwtError") return "session_expired"
  if (name === "AuthRetryableFetchError") return status === 429 ? "rate_limited" : "network"
  if (status === 429) return "rate_limited"

  return classifyByText(message) ?? "unknown"
}

/** O mínimo de caracteres que o serviço citou, quando citou. */
function minimumLengthFrom(error: unknown): number | null {
  const match = /at least (\d+) characters?/i.exec(asString(asObject(error).message))
  if (!match) return null
  const n = Number(match[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * A frase que a tela mostra. O erro original não aparece nela: quem chama
 * registra o erro no console antes.
 */
export function describeAuthError(error: unknown, context: AuthErrorContext): string {
  const kind = classifyAuthError(error)
  switch (kind) {
    case "invalid_credentials":
      return context === "change_password"
        ? "A senha atual não confere. Digite de novo a senha que você usa para entrar."
        : "E-mail ou senha incorretos. Confira os dois e tente de novo; se esqueceu a senha, use “Esqueci minha senha”."
    case "leaked_password":
      return "Esta senha já apareceu em vazamentos de dados na internet e pode ser descoberta por outras pessoas. Escolha outra senha, de preferência uma que você não use em nenhum outro site."
    case "password_too_short": {
      const min = minimumLengthFrom(error)
      return min
        ? `A senha é curta demais. Use pelo menos ${min} caracteres.`
        : "A senha é curta demais. Escolha uma senha mais longa."
    }
    case "password_needs_variety":
      return "A senha precisa misturar tipos diferentes de caractere: letras maiúsculas e minúsculas, números e símbolos. Escolha outra senha."
    case "weak_password":
      return "Esta senha é fácil de adivinhar. Escolha uma senha mais longa, que misture letras, números e símbolos."
    case "same_password":
      return "A senha nova é igual à atual. Escolha uma senha diferente."
    case "rate_limited":
      return "Foram muitas tentativas em pouco tempo. Espere alguns minutos e tente de novo."
    case "email_not_confirmed":
      return "Este e-mail ainda não foi confirmado. Abra o link de confirmação que enviamos para ele (confira também a caixa de spam) e tente de novo."
    case "email_in_use":
      return "Este e-mail já é usado por outro acesso ao CreditOS. Escolha outro e-mail; se este é seu, fale com o administrador da sua unidade."
    case "email_invalid":
      return "Este e-mail não foi aceito. Confira se está escrito certo e tente de novo."
    case "session_expired":
      return context === "set_password" ? INVITE_LINK_EXPIRED_MESSAGE : SESSION_EXPIRED_MESSAGE
    case "access_blocked":
      return "Este acesso está bloqueado. Fale com o administrador da sua unidade."
    case "network":
      return "Não consegui falar com o servidor. Confira sua conexão com a internet e tente de novo."
    case "unknown":
      return GENERIC_BY_CONTEXT[context]
  }
}

/** Recusas que dizem respeito à senha nova escolhida (para mostrar junto ao campo). */
export function isNewPasswordProblem(kind: AuthErrorKind): boolean {
  return (
    kind === "leaked_password" ||
    kind === "password_too_short" ||
    kind === "password_needs_variety" ||
    kind === "weak_password" ||
    kind === "same_password"
  )
}
